import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  find, get, getOr, map, toString,
} from 'lodash/fp';
import {
  FormControl, InputLabel, MenuItem, OutlinedInput, Paper, Popper, Select,
} from '@material-ui/core';
import { ChevronRight, Settings } from '@material-ui/icons';
import { FormattedMessage } from 'react-intl';
import messages from '../../messages';

const S = {
  FormControl: styled(FormControl)`
    width: 100%;
  `,
  InputLabel: styled(InputLabel)`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Select: styled(({ classes, ...otherProps }) => (
    <Select
      classes={{
        icon: 'icon',
        select: 'select',
      }}
      {...otherProps}
    />
  ))`
    .icon {
      right: 0.5rem;
    }
    .select {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 2rem 0 0.875rem;
      color: ${({ theme }) => theme.palette.grey[800]};
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      font-weight: 400;
    }
    &.Mui-focused .icon {
      color: ${({ theme }) => theme.palette.primary.main};
    }
    :hover {
      .icon {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  `,
  OutlinedInput: styled(({ classes, ...otherProps }) => (
    <OutlinedInput
      classes={{
        ...classes,
        notchedOutline: 'notchedOutline',
      }}
      {...otherProps}
    />
  ))`
    .notchedOutline {
      border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  MenuItem: styled(MenuItem)`
    position: relative;
    min-width: 200px;
    min-height: 40px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    &.Mui-selected:hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
  ChevronRight: styled(ChevronRight)`
    margin-left: auto;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  Popper: styled(Popper)`
    z-index: 1300;
  `,
  Paper: styled(Paper)`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(100%, -8px);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.12);
  `,
  Suboptions: styled.ul`
    padding: 8px 0;
  `,
  Settings: styled(Settings)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
};

const GroupFilter = ({
  className, labels, open, onOpen, onClose, options, value, onChange, onSubgroupChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [active, setActive] = useState(false);
  const [labelWidth, setLabelWidth] = useState(0);

  const inputLabel = useRef(null);

  const measuredRef = useCallback((node) => {
    if (node) {
      inputLabel.current = node;
    }
  }, []);

  const MenuProps = useMemo(() => ({
    onEntered: () => setActive(true),
    onExit: () => {
      setAnchorEl(null);
      setActive(false);
    },
  }), []);

  const renderValue = useCallback(selectedValue => (
    selectedValue === ''
      ? ''
      : getOr('', 'label', find(label => label.value === selectedValue, labels))
  ), [labels]);
  const showSuboptions = useCallback(event => setAnchorEl(event.currentTarget), []);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  return (
    <S.FormControl className={className} variant="outlined">
      <S.InputLabel ref={measuredRef} margin="dense">
        그룹
      </S.InputLabel>
      <S.Select
        renderValue={renderValue}
        value={value}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        onChange={onChange}
        MenuProps={MenuProps}
        input={<S.OutlinedInput labelWidth={labelWidth} name="select" id="select" />}
      >
        {map(option => (
          <S.MenuItem
            key={option.id}
            id={option.id}
            value={option.value}
            data-type={option.type}
            onMouseOver={showSuboptions}
            onFocus={showSuboptions}
          >
            {option.label}
            {option.suboptions && <S.ChevronRight />}
            {option.suboptions && active && (
              <S.Popper
                placement="top-end"
                anchorEl={anchorEl}
                open={get('id', anchorEl) === toString(option.id)}
              >
                <S.Paper>
                  <S.Suboptions>
                    {map(suboption => (
                      <S.MenuItem
                        key={suboption.id}
                        data-type={suboption.type}
                        data-value={`${suboption.type}_${suboption.value}`}
                        onClick={onSubgroupChange}
                      >
                        {suboption.label}
                      </S.MenuItem>
                    ), option.suboptions)}
                  </S.Suboptions>
                </S.Paper>
              </S.Popper>
            )}
          </S.MenuItem>
        ), options)}
        <S.MenuItem value="settings">
          <S.Settings />
          <FormattedMessage {...messages.groupFilterSettings} />
        </S.MenuItem>
      </S.Select>
    </S.FormControl>
  );
};

GroupFilter.propTypes = {
  /** @ignore */
  className: PropTypes.string,
  labels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node,
      suboptions: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubgroupChange: PropTypes.func.isRequired,
};

GroupFilter.defaultProps = {
  className: undefined,
  labels: [],
  options: [],
};

export default GroupFilter;
